export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Copy the own enumerable properties of every source onto `out`, skipping falsy sources.
 * `out` is mutated and returned.
 */
export function extend<T extends object, U extends object>(out: T, source: U): T & U;
export function extend<T extends object, U extends object, V extends object>(
  out: T,
  source1: U,
  source2: V,
): T & U & V;
export function extend<T extends object, U extends object, V extends object, W extends object>(
  out: T,
  source1: U,
  source2: V,
  source3: W,
): T & U & V & W;
export function extend<T extends object>(
  out: T,
  ...sources: readonly (object | null | undefined)[]
): T;
export function extend(
  out: Record<string, unknown> | null | undefined,
  ...sources: readonly (object | null | undefined)[]
): Record<string, unknown> {
  const target = out || {};
  sources.forEach((source) => {
    if (!source) return;
    Object.keys(source).forEach((key) => {
      target[key] = (source as Record<string, unknown>)[key];
    });
  });
  return target;
}

/** Structural equality; object key order is ignored, array order is significant. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => deepEqual(value, b[index]));
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false;

  const keysA = Object.keys(a).filter((key) => a[key] !== undefined);
  const keysB = Object.keys(b).filter((key) => b[key] !== undefined);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => deepEqual(a[key], b[key]));
}

/**
 * Loose structural equality: arrays and objects are compared by their keys only, so `[1]` equals
 * `{ 0: 1 }`, and keys holding `undefined` count. Prefer {@link deepEqual} for new code.
 */
export const looseDeepEqual = (x: unknown, y: unknown): boolean => {
  const ok = Object.keys;
  const tx = typeof x;
  const ty = typeof y;
  if (x && y && tx === 'object' && tx === ty) {
    const xObj = x as Record<string, unknown>;
    const yObj = y as Record<string, unknown>;
    return (
      ok(xObj).length === ok(yObj).length &&
      ok(xObj).every((key) => looseDeepEqual(xObj[key], yObj[key]))
    );
  }
  return x === y;
};
